import { MainContext, mainDefaultState } from '../../context/main.context';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import Loader from '../loader';
import Protected from '../../hoc/Protected';
import AuthMain from '../auth';
import Registration from '../auth/email/registration';
import LoginByMail from '../auth/email/login';
import RecoveryPassword from '../auth/email/recovery-password';
import Payments from '../payments';
import PaymentsYookassa from '../payments/yookassa';
import PaymentsStripe from '../payments/stripe';
import React, { useEffect, useState } from 'react';
import {
    deleteUserAuthDataId,
    getUserAuthData,
    loginUser,
    logOutUser,
} from '../../common/tools';
import { toast } from 'react-toastify';
import { GET_INITIAL_CONFIG_ERROR } from '../../common/errorCodes';
import { getInitialConfig } from '../../stores/control.stores';
import Prices from '../prices';
import DeletionPersonalData from '../auth/deletionPersonalData';
import GamesMain from '../games';
import Game from '../games/game';
import { redirectToGame } from '../../hoc/ManualController';
import i18n from 'i18next';
import Transactions from '../transactions';
import TransactionHelper from '../transactions/helper/TransactionHelper';
import PaymentsYandexPay from '../payments/yandex_pay';
import ModalGlobal from '../../hoc/ModalGlobal';
import { GlobalModalProps, IProjectSettingExtra, IWebAuthData, PaymentPopup, Settings } from '../../interfaces';
import AuthState from '../../hoc/AuthState';
import { PaymentStateRevocable, PaymentStatePaid, PaymentStatePending } from '../../hoc/PaymentState';
import SingleGame from '../games/single';
import { useNavigate } from 'react-router-dom';
import { LOCALES } from '../../common/constants';

export let _session: string | null = null;
export let _jwt: string | null = null;
export let _source: string | null = null;
export let _locale: string | null = null;
export let _project: string | null = null;


export default function MainProvider() {
    const [ searchParams ] = useSearchParams();
    const navigate = useNavigate();
    const { language } = i18n;

    const [ session, _setSession ] = useState<string | null>(searchParams.get('session'));
    const [ source, _setSource ] = useState<string | null>(searchParams.get('source'));
    const [ locale, _setLocale ] = useState<string | null>(searchParams.get('locale'));
    const [ project, _setProject ] = useState<string | null>(searchParams.get('project'));
    const [ jwt, _setJwt ] = useState<string | null>(searchParams.get('jwt'));
    const [ paymentId, setPaymentId ] = useState<string | null>(searchParams.get('id'));

    const [ settings, setSettings ] = useState<Settings | null>(null);
    const [ showCaptcha, setShowCaptcha ] = useState<boolean>(mainDefaultState.showCaptcha);
    const [ limit, setLimit ] = useState<number>(5);
    const [ count, setCount ] = useState<number>(Number(localStorage.getItem('attempt')) || 0);
    const [ loader, setLoader ] = useState<boolean>(mainDefaultState.loader);
    const [ modalContent, setModalContent ] = useState<GlobalModalProps | null>(mainDefaultState.modalContent);
    const [ userAuthData, setUserAuthData ] = useState<IWebAuthData | null>(getUserAuthData());
    const [ paymentPopupData, setPaymentPopupData ] = useState<PaymentPopup | null>(null);

    const { checkUserAuth } = AuthState();

    useEffect(() => {
        _getInitialConfig();

        const correctLanguageInUrl = () => {
            const currentPath = window.location.pathname;
            const pathParts = currentPath.split('/');

            if (!pathParts.includes('games')) {
                return;
            }

            if (pathParts.length > 2 && pathParts[1] === 'games') {
                const localeAfterGames = pathParts[2];

                if (!LOCALES.includes(localeAfterGames)) {
                    pathParts[2] = language;

                    const newPath = pathParts.join('/');
                    navigate(newPath);
                }
            }
        };

        correctLanguageInUrl();

        i18n.on('languageChanged', (lng: string) => {
            window.localStorage.setItem('i18nextLng', lng);

            const currentPath = window.location.pathname;
            const pathParts = currentPath.split('/');

            if (!pathParts.includes('games')) {
                return;
            }

            if (pathParts[1] === 'games' && pathParts.length > 2) {
                pathParts[2] = lng;
            }

            const newPath = pathParts.join('/');
            navigate(newPath);

            _getInitialConfig();
        });

        return () => {
            i18n.off('languageChanged');
        };
    }, []);

    const toggleLoader = (value: boolean): void => setLoader(value);

    const setUserAuth = (userAuthData: IWebAuthData | null, token: string | '') => {
        if (userAuthData && token) {
            loginUser(userAuthData, token);
            setUserAuthData(userAuthData);
            deleteUserAuthDataId();
            return;
        }

        logOutUser();
        setUserAuthData(null);
    }

    const setSession = (value: string | null) => {
        _setSession(value);
        _session = value;
    }

    const setJwt = (value: string | null) => {
        _setJwt(value);
        _jwt = value;
    }

    const setSource = (value: string | null) => {
        _setSource(value);
        _source = value;
    }

    const setLocale = (value: string | null) => {
        _setLocale(value);
        _locale = value;
    }

    const setProject = (value: string | null) => {
        _setProject(value);
        _project = value;
    }

    useEffect((): void => {
        const failedAttemptLimit =
            settings?.projects?.extra?.find((elem: IProjectSettingExtra) => elem.id === 'failedAttemptLimit');

        if (typeof failedAttemptLimit === 'number') {
            setLimit(failedAttemptLimit);
        }
    }, [ settings ]);

    useEffect(() => {
        if (settings) {
            checkUserAuth(settings?.projects?.authType);
        }
    }, [ settings ]);

    useEffect((): void => {
        if (!settings) {
            return;
        }

        if (!showCaptcha && count >= limit) {
            setShowCaptcha(true);
            return
        }

        if (showCaptcha && count === 0) {
            setShowCaptcha(false);
        }
    }, [ limit, count, settings ]);

    const failedAttemptCenter = (type: 'add'|'reset'): void => {
        if (type === 'add') {
            const newCountValue: number = count + 1;

            localStorage.setItem('attempt', String(newCountValue));
            setCount(newCountValue);
            return;
        }

        if (type === 'reset') {
            const newCountValue: number = 0;

            localStorage.setItem('attempt', String(newCountValue));
            setCount(newCountValue);
        }
    }

    const _getInitialConfig = (): void => {
        getInitialConfig().then(res => {
            const result = res?.data;

            if (result) {
                setSettings(result);
                return;
            }

            // Для веба нужно будет доп параметр добавить, на случай если бекенд недоступен,
            // будет отправлено сообщение в postMessage
            // По дефолту выполняем редирект

            if (session) {
                redirectToGame({ res }, 'auth', true, GET_INITIAL_CONFIG_ERROR);
                return;
            }

            toast.error('GET_INITIAL_CONFIG_ERROR');
        }, err => {
            const errData = err?.response?.data || err;

            if (session) {
                redirectToGame({ errData }, 'auth', true, GET_INITIAL_CONFIG_ERROR);
                return;
            }

            toast.error('GET_INITIAL_CONFIG_ERROR');
        });
    }

    if (!settings) {
        return null;
    }

    const contextValue = {
        session,
        source,
        locale,
        project,
        settings,
        paymentId,
        jwt,
        showCaptcha,
        failedAttemptCenter,
        loader,
        toggleLoader,
        modalContent,
        setModalContent,
        setSession,
        setSource,
        setLocale,
        setProject,
        setPaymentId,
        setJwt,
        userAuthData,
        setUserAuth,
        paymentPopupData,
        setPaymentPopupData,
    }

    const isOutOfCatalog: boolean | undefined = settings?.projects?.mainData?.outOfCatalog;
    
    return (
        <MainContext.Provider value={contextValue}>
            { modalContent && <ModalGlobal/> }
            { loader && <Loader/> }
            <PaymentStatePending/>
            <PaymentStatePaid/>
            <PaymentStateRevocable/>
            <Routes>
                {/*Session*/}
                <Route path="/session/loginDone/:result?" element={<Loader/>}/>
                <Route path="/session/login/:session?/:source?/:locale?/:project?/:jwt?" element={<Protected><AuthMain/></Protected>}/>
                <Route path="/session/registration/:session?/:source?" element={<Protected><Registration/></Protected>}/>
                <Route path="/session/login/email/:session?/:source?" element={<Protected><LoginByMail/></Protected>}/>
                {/*Auth*/}
                <Route path="/auth/recovery-password/:session?/:source?" element={<Protected><RecoveryPassword/></Protected>}/>
                <Route path="/auth/deletion-personal-data" element={<DeletionPersonalData/>}/>
                {/*Payments*/}
                <Route path="/payments/process/:id?/:source?/:locale?/:project?/:jwt?" element={<Payments/>}/>
                <Route path="/payments/yookassa/:id?/:source?/:locale?/:project?/:jwt?" element={<PaymentsYookassa/>}/>
                <Route path="/payments/stripe/:id?/:source?/:locale?/:project?/:jwt?" element={<PaymentsStripe/>}/>
                <Route path="/payments/yandex-pay/:id?/:source?/:locale?/:project?/:jwt?" element={<PaymentsYandexPay/>}/>
                <Route path="/payments/done/:result?" element={<Loader/>}/>
                {/*Games*/}
                {
                    !isOutOfCatalog ?
                        <>
                            <Route path={`/games/:lng/:projectName/`} element={<Game/>}/>
                            <Route path={`/games/:lng/`} element={<GamesMain/>}/>
                        </>
                    :
                        <Route path={`/games/:lng/`} element={<SingleGame/>}/>
                }
                {/*Transactions*/}
                <Route path="/transactions" element={<Transactions/>}/>
                <Route path="/transactions/help/:id?" element={<TransactionHelper/>}/>
                {/*Main*/}
                <Route path="/unauthorized/:result?" element={<Loader/>}/>
                <Route path="/loader" element={<Loader/>}/>
                <Route path="/prices/:project?" element={<Prices/>}/>
                <Route path="*" element={<Navigate to={`/games/${language}/`} replace={true}/>}/>
            </Routes>
        </MainContext.Provider>
    );
}